import React, { useEffect, useMemo } from "react";
import { css, useTheme } from "@emotion/react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router";
import { useAppState } from "core/contexts/AppContext";
import useBreakpoint from "core/hooks/useBreakpoint";
import useRouter from "core/hooks/useRouter";
import ErrorPage from "pages/Components/ErrorPage/ErrorPage";
import pages from "pages/config/pages";

import { isBetween, isDown } from "theme/breakpoints";
import useMetadata, {MetaData} from "core/hooks/useMetadata";

type Props = {
    stage: string | null,
    showBreadcrumb: boolean,
};

/**
 * Handles the main rendering of pages.  Returns error pages if needed.
 * @constructor
 */
const Main: React.FC<Props> = ({ stage, showBreadcrumb }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const [, setAppState] = useAppState();
    const { breakpoint } = useBreakpoint();
    const extraMenuHeight = useMemo(() => {
        switch (true) {
            case !showBreadcrumb:
                return 0;
            case isBetween(breakpoint, "sm", "lg"):
                return theme.sizes.menu.breadcrumbBurgerHeight;
            case isDown(breakpoint, "xs"):
                return theme.sizes.menu.breadcrumbBurgerHeight + theme.sizes.menu.breadcrumbBurgerSmallHeight;
            default:
                return theme.sizes.menu.breadcrumbHeight;
        }
    }, [
        breakpoint,
        showBreadcrumb,
        theme.sizes.menu.breadcrumbHeight,
        theme.sizes.menu.breadcrumbBurgerHeight,
        theme.sizes.menu.breadcrumbBurgerSmallHeight,
    ]);

    const style = css`
        padding-top: ${theme.sizes.menu.heightMain + extraMenuHeight}px;

        ${theme.breakpoints.down("lg")} {
            padding-top: ${theme.sizes.menu.burgerHeightMain + extraMenuHeight}px;
        }
    `;

    const [data, loading, error] = useRouter(stage);
    const page = data?.page;
    const metaData = useMetadata(page);

    useEffect(() => {
        // loading is used for displaying <Footer>
        setAppState({ loading });
    }, [loading, setAppState]);

    if (page?.__typename === 'RedirectorPage') {
        if (page.linkUrl.indexOf('http') === 0) {
            window.location = page.linkUrl;
        } else {
            navigate(page.linkUrl);
        }
        return null;
    }

    let Component = page && pages.hasOwnProperty(page.__typename) && pages[page.__typename];

    return (
        <div css={style}>
            {(error || (page && !page.isClickable)) && (
                <ErrorPage error={error === "404" ? error : "500"} />
            )}
            {(!error && page && page.isClickable && Component) && (
                <>
                    <Helmet>
                        {metaData.map((meta: MetaData, index) => {
                            switch (meta.element) {
                                case 'link': return <link key={`meta-key-${index}`} {...meta.props} />;
                                case 'title': return <title key={`meta-key-${index}`} {...meta.props} />;
                                default: return <meta key={`meta-key-${index}`} {...meta.props} />;
                            }
                        })}
                    </Helmet>

                    <Component {...page && { data: page }} />
                </>
            )}
        </div>
    );
};

export default Main;
